import { useNavigate } from "@solidjs/router";
import { createSignal, Show } from "solid-js";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { ScreenLoader, Spinner } from "../components/Spinner";
import { TextField } from "../components/TextField";
import { session } from "../state/session";

type Tab = "account" | "linked";

/** Figma G0 (signed out) and G0L (connected): "Mon compte" / "Comptes liés" tabs. */
export function Account() {
  const navigate = useNavigate();
  const [tab, setTab] = createSignal<Tab>("account");
  const [leaving, setLeaving] = createSignal(false);
  const signOut = async () => {
    setLeaving(true);
    try {
      await session.signOut();
      navigate("/", { replace: true });
    } finally {
      setLeaving(false);
    }
  };
  return (
    <main class="screen bg-surface">
      <AppBar title="Mon compte" back="/" />
      <AccountTabs tab={tab()} onChange={setTab} />
      <Show
        when={tab() === "account"}
        fallback={
          // Linked accounts (Pony) are out of the PoC scope.
          <p class="px-5 pt-8 text-center text-[15px] leading-[1.25] tracking-[0.15px] text-text">
            Bientôt disponible
          </p>
        }
      >
        <Show when={!session.me.loading || session.me()} fallback={<ScreenLoader />}>
          <div class="screen-body px-[15px] pt-5">
            <TextField
              id="mticket-id"
              label="Identifiant M'Ticket :"
              readOnly
              value={session.me()?.id ?? ""}
              placeholder="Attribué à la connexion"
            />
            <Show
              when={session.me()}
              fallback={
                <>
                  <div class="mt-[18px] text-[13px] font-medium leading-[1.25] tracking-[0.13px] text-strong">
                    Compte mail :
                  </div>
                  <p class="mt-1 text-[11px] leading-[1.25] tracking-[0.11px] text-text">
                    Ajouter une adresse mail pour conserver vos données et titres M'Ticket
                    (e-tickets, moyens de paiement, véhicules, etc.) ou en cas de changement de
                    téléphone
                  </p>
                  <Button class="mt-[3px]" onClick={() => navigate("/signin")}>
                    Se connecter avec un e-mail
                  </Button>
                </>
              }
            >
              {(m) => (
                <>
                  <TextField
                    id="mail"
                    label="Compte mail :"
                    class="mt-3"
                    readOnly
                    value={m().email}
                  />
                  <span class="mt-[14px] inline-flex h-[26px] w-[120px] items-center gap-[5px] rounded-[13px] bg-surface2 pl-2 text-xs font-medium leading-[1.25] tracking-[0.12px] text-violet">
                    <img src="/icons/check-circle-16.svg" width="16" height="16" alt="" />
                    Email vérifié
                  </span>
                  {/* Not in Figma: a way out of the PoC session. */}
                  <div class="screen-footer flex justify-center">
                    <button
                      type="button"
                      class="flex items-center gap-2 py-2 text-sm text-text underline disabled:no-underline"
                      disabled={leaving()}
                      onClick={() => void signOut()}
                    >
                      <Show when={leaving()}>
                        <Spinner size={16} class="text-violet" />
                      </Show>
                      Se déconnecter
                    </button>
                  </div>
                </>
              )}
            </Show>
          </div>
        </Show>
      </Show>
    </main>
  );
}

/** Figma tam/tabs: 58 px, Medium 19, violet active label with a 104 x 3 indicator, grey divider. */
function AccountTabs(props: { tab: Tab; onChange: (t: Tab) => void }) {
  const item = (id: Tab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={props.tab === id}
      class={`relative h-[57px] pt-4 text-[19px] font-medium leading-[1.25] tracking-[0.19px] self-start ${props.tab === id ? "text-violet" : "text-strong"}`}
      onClick={() => props.onChange(id)}
    >
      <span class="block">{label}</span>
      <Show when={props.tab === id}>
        <span class="absolute bottom-0 left-1/2 h-[3px] w-[104px] -translate-x-1/2 rounded-t-[3px] bg-violet" />
      </Show>
    </button>
  );
  return (
    <div class="shrink-0" role="tablist">
      <div class="grid h-[57px] grid-cols-2">
        {item("account", "Mon compte")}
        {item("linked", "Comptes liés")}
      </div>
      <div class="mx-[15px] h-px bg-border" />
    </div>
  );
}
